import { supabase } from './supabase';
import { API_BASE_URL } from '../config/apiUrl';

/**
 * Push a senior's configuration from Supabase to Firebase so the device picks
 * it up. Single source of truth for `POST /api/seniors/:seniorId/sync-config`.
 *
 * SECURITY (2026-08-11): that route used to accept any `seniorId` with NO
 * authentication, so anyone could push a config sync to any senior's phone.
 * It now requires a Supabase JWT and verifies the senior belongs to the caller.
 * Six call sites posted it bare — SettingsDrawer, ContactsDrawer,
 * ScamBlockingDrawer, ManagedModeDrawer, EditConfigModal, EditContactsModal —
 * and all six would 401 if they kept doing so. They all go through here now.
 *
 * Unlike the billing portal, the id CANNOT be dropped from the request: the
 * caller is choosing which of its own seniors to sync, so the server verifies
 * ownership instead of inferring the target.
 *
 * NEVER THROWS. Every caller treats sync as best-effort — the database write
 * has already happened and an offline phone picks the change up on its next
 * sync — so a failure here must not roll back UI that is already correct.
 * Returns whether the push succeeded, for callers that want to say so.
 */
export async function syncSeniorConfig(seniorId: string): Promise<boolean> {
  if (!seniorId) return false;
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    const token = session?.access_token;
    if (!token) {
      console.warn('[syncConfig] No session — skipping device sync.');
      return false;
    }

    const res = await fetch(`${API_BASE_URL}/seniors/${seniorId}/sync-config`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
    if (!res.ok) {
      console.warn(`[syncConfig] Device sync failed (${res.status}) — the change is saved; the phone picks it up on next sync.`);
      return false;
    }
    return true;
  } catch (err) {
    // Offline / DNS / CORS. Same reasoning: the DB already has the change.
    console.warn('[syncConfig] Device sync failed (device may be offline):', err);
    return false;
  }
}
